import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useAuthContext } from "../../providers/Auth/auth.provider";
import { AvailableBaseRoutes } from "../../utils/constants";

interface IDashboardActionContextType {
  contractId: string | undefined;
  setContractId: (contractId: string | undefined) => void;
}

interface IDashboardActionContextProps {
  children: ReactNode;
}

const DashboardActionContext = createContext<IDashboardActionContextType>({} as IDashboardActionContextType);

export const DashboardActionProvider = (props: IDashboardActionContextProps) => {
  const value = useDashboardActionProvider(props);
  return <DashboardActionContext.Provider value={value}>{props.children}</DashboardActionContext.Provider>;
};

export const useDashboardActionContext = () => {
  return useContext(DashboardActionContext);
};

const useDashboardActionProvider = (_props: IDashboardActionContextProps): IDashboardActionContextType => {
  const [contractId, setContractId] = useState<string | undefined>(undefined);

  const { internalUser } = useAuthContext();
  const location = useLocation();

  // Reset contract selection when the user navigates back to the dashboard so they
  // must re-select a contract when re-entering the reporting view.
  useEffect(() => {
    if (location.pathname === AvailableBaseRoutes.Portal) {
      setContractId(undefined);
    }
  }, [location.pathname]);

  // Auto-select when there is exactly one active contract (or after a reset).
  useEffect(() => {
    if (contractId !== undefined) return;
    const activeContracts = internalUser?.group?.pomContracts?.filter((c) => c.active) ?? [];
    if (activeContracts.length === 1) {
      setContractId(activeContracts[0].contractNumber);
    }
  }, [internalUser, contractId]);

  return {
    contractId,
    setContractId,
  };
};
